import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from "./api";
import { NotificationIcon } from "./NotificationIcon";

const PER_PAGE = 20;

const CATEGORIES = [
  "leave",
  "document",
  "task",
  "shift",
  "schedule",
  "infraction",
  "contract",
  "security",
  "system",
] as const;

export function NotificationsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();

  const page = Math.max(1, Number(searchParams.get("page") || "1"));
  const category = searchParams.get("category") || "";
  const unreadOnly = searchParams.get("unread_only") === "true";

  const query = useQuery({
    queryKey: ["notifications", { page, unreadOnly }],
    queryFn: () =>
      fetchNotifications({
        limit: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
        unread_only: unreadOnly,
      }),
  });

  const filtered = useMemo<NotificationItem[]>(() => {
    if (!query.data) return [];
    if (!category) return query.data.notifications;
    return query.data.notifications.filter((n) => n.category === category);
  }, [query.data, category]);

  const totalPages = useMemo(() => {
    if (!query.data) return 1;
    return Math.max(1, Math.ceil(query.data.total / PER_PAGE));
  }, [query.data]);

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next);
  };

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
    },
  });

  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
    },
  });

  return (
    <div className="notifications-root" style={{ maxWidth: 800, margin: "0 auto" }}>
      <div className="notifications-header">
        <div>
          <h1 className="page-title">{t("notif.title")}</h1>
          <p className="page-subtitle">{t("notif.subtitle")}</p>
        </div>
        {query.data && query.data.unread_count > 0 && (
          <div className="notifications-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
            >
              {t("notif.mark_all")}
            </button>
          </div>
        )}
      </div>

      <div className="card notifications-filters">
        <div className="leave-filter-chips">
          <button
            type="button"
            className={"chip chip-toggle" + (category === "" ? " chip-active" : "")}
            onClick={() => updateParams({ category: null, page: null })}
          >
            {t("notif.cat.all")}
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={
                "chip chip-toggle" + (category === cat ? " chip-active" : "")
              }
              onClick={() => updateParams({ category: cat, page: null })}
            >
              {t(`notif.cat.${cat}`)}
            </button>
          ))}
        </div>
        <label className="notifications-unread-toggle">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) =>
              updateParams({
                unread_only: e.target.checked ? "true" : null,
                page: null,
              })
            }
          />
          <span>{t("notif.unread_only")}</span>
        </label>
      </div>

      <div className="card">
        {query.isLoading && <p style={{ padding: 16 }}>...</p>}
        {query.data && filtered.length === 0 && (
          <div className="notifications-empty" style={{ textAlign: "center", padding: "48px 16px" }}>
            <p className="page-subtitle" style={{ margin: 0 }}>
              {t("notif.empty")}
            </p>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="notifications-list">
            {filtered.map((n) => (
              <button
                key={n.id}
                type="button"
                className={
                  "notification-item" +
                  (!n.read_at ? " notification-unread" : "")
                }
                onClick={() => {
                  if (!n.read_at) markRead.mutate(n.id);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  border: 0,
                  background: "transparent",
                }}
              >
                <NotificationIcon category={n.category} />
                <div className="notification-content">
                  <div className="notification-title-row">
                    {!n.read_at && <span className="notification-dot" />}
                    <strong>{n.title}</strong>
                  </div>
                  <div className="notification-body">{n.body}</div>
                  {n.actor_name && (
                    <div className="notification-actor summary-card-meta">
                      {n.actor_name}
                    </div>
                  )}
                </div>
                <div className="notification-time">
                  {formatRelativeTime(n.created_at)}
                </div>
              </button>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div
            className="pagination"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              padding: "12px 16px",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => updateParams({ page: String(page - 1) })}
            >
              «
            </button>
            <span className="summary-card-meta">
              {t("notif.page", { page, total: totalPages })}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => updateParams({ page: String(page + 1) })}
            >
              »
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "ahora";
    if (diff < 3600) return Math.floor(diff / 60) + " min";
    if (diff < 86400) return Math.floor(diff / 3600) + "h";
    if (diff < 604800) return Math.floor(diff / 86400) + "d";
    return new Intl.DateTimeFormat(undefined, { dateStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
}
